import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import { Toaster } from 'sonner';

// Auth Pages
import LandingPage from '@/pages/LandingPage';
import AuthCallback from '@/pages/AuthCallback';
import PublicEventPage from '@/pages/PublicEventPage';
import PublicVotingPage from '@/pages/PublicVotingPage';

// Member Pages
import MemberLayout from '@/layouts/MemberLayout';
import HomePage from '@/pages/member/HomePage';
import MembersListPage from '@/pages/member/MembersListPage';
import MemberDetailPage from '@/pages/member/MemberDetailPage';
import EventsPage from '@/pages/member/EventsPage';
import EventDetailPage from '@/pages/member/EventDetailPage';
import GalleryPage from '@/pages/member/GalleryPage';
import ProfilePage from '@/pages/member/ProfilePage';
import PaymentsPage from '@/pages/member/PaymentsPage';

// Admin Pages
import AdminLayout from '@/layouts/AdminLayout';
import DashboardPage from '@/pages/admin/DashboardPage';
import AdminMembersPage from '@/pages/admin/AdminMembersPage';
import AdminEventsPage from '@/pages/admin/AdminEventsPage';
import MediaModerationPage from '@/pages/admin/MediaModerationPage';
import NotificationsPage from '@/pages/admin/NotificationsPage';
import SettingsPage from '@/pages/admin/SettingsPage';
import AdminBankDetailsPage from '@/pages/admin/AdminBankDetailsPage';
import AdminNewsPage from '@/pages/admin/AdminNewsPage';
import AdminBlogsPage from '@/pages/admin/AdminBlogsPage';
import AdminFormsPage from '@/pages/admin/AdminFormsPage';
import AdminFormResponsesPage from '@/pages/admin/AdminFormResponsesPage';
import AdminVotesPage from '@/pages/admin/AdminVotesPage';
import AdminBackupPage from '@/pages/admin/AdminBackupPage';
import AdminVoteRecoveryPage from '@/pages/admin/AdminVoteRecoveryPage';
import VoteLinkDesigner from '@/pages/admin/design/VoteLinkDesigner';
import CertificateDesigner from '@/pages/admin/design/CertificateDesigner';
import DynamicFlyerDesigner from '@/pages/admin/design/DynamicFlyerDesigner';
import PublicFlyerPage from '@/pages/PublicFlyerPage';
import AnalyticsPage from '@/pages/admin/AnalyticsPage';

import ProtectedRoute from '@/components/ProtectedRoute';

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/event/:eventId" element={<PublicEventPage />} />
      <Route path="/vote/:submissionId" element={<PublicVotingPage />} />
      <Route path="/flyer/:flyerId" element={<PublicFlyerPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      <Route path="/app" element={<ProtectedRoute><MemberLayout /></ProtectedRoute>}>
        <Route index element={<HomePage />} />
        <Route path="members" element={<MembersListPage />} />
        <Route path="members/:userId" element={<MemberDetailPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="events/:eventId" element={<EventDetailPage />} />
        <Route path="gallery" element={<GalleryPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="payments" element={<PaymentsPage />} />
      </Route>
      
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="members" element={<AdminMembersPage />} />
        <Route path="events" element={<AdminEventsPage />} />
        <Route path="media" element={<MediaModerationPage />} />
        <Route path="news" element={<AdminNewsPage />} />
        <Route path="blogs" element={<AdminBlogsPage />} />
        <Route path="forms" element={<AdminFormsPage />} />
        <Route path="form-responses" element={<AdminFormResponsesPage />} />
        <Route path="votes" element={<AdminVotesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="bank-details" element={<AdminBankDetailsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="backup" element={<AdminBackupPage />} />
        <Route path="vote-recovery" element={<AdminVoteRecoveryPage />} />
        <Route path="design/vote-link" element={<VoteLinkDesigner />} />
        <Route path="design/certificate" element={<CertificateDesigner />} />
        <Route path="design/flyer" element={<DynamicFlyerDesigner />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Router>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </Router>
    </div>
  );
}

export default App;
